import { skipToken, useQuery } from '@tanstack/react-query';

import { Agents } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { shouldRetryPrivateWorkspaceAgentSessionGet } from '~/components/v2/components/workspace/workspace-agent-panel/utils/shouldRetryPrivateWorkspaceAgentSessionGet';

export const PRIVATE_WORKSPACE_AGENT_SESSION_GET_KEY = 'PRIVATE_WORKSPACE_AGENT_SESSION_GET';

export type PrivateWorkspaceAgentSessionGetParams = Omit<
  NonNullable<Parameters<typeof Agents.Sessions.get>[0]>,
  'agentType' | 'workspaceId' | 'sessionId'
> & {
  sessionId: string | undefined;
  workspaceId: string | undefined;
};

export type PrivateWorkspaceAgentSessionGetKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SESSION_GET_KEY,
  PrivateWorkspaceAgentSessionGetParams,
];

export const getPrivateWorkspaceAgentSessionGetKey = (
  params: PrivateWorkspaceAgentSessionGetParams,
): PrivateWorkspaceAgentSessionGetKey => [PRIVATE_WORKSPACE_AGENT_SESSION_GET_KEY, params];

export type UsePrivateWorkspaceAgentSessionGetOptions = {
  enabled?: boolean;
};

/**
 * The session itself, which unlike the message list carries the elicitations the agent is currently
 * blocked on. Those are only announced over the socket as they happen, so this is the sole way to
 * learn about one raised before the panel was watching.
 */
export const usePrivateWorkspaceAgentSessionGet = (
  params: PrivateWorkspaceAgentSessionGetParams,
  options?: UsePrivateWorkspaceAgentSessionGetOptions,
) => {
  const { sessionId, workspaceId } = params;

  return useQuery({
    queryKey: getPrivateWorkspaceAgentSessionGetKey(params),
    queryFn:
      !workspaceId || !sessionId
        ? skipToken
        : () => callAirApi(() => Agents.Sessions.get({ agentType: 'workspace', ...params, sessionId, workspaceId })),
    enabled: !!workspaceId && !!sessionId && (options?.enabled ?? true),
    retry: shouldRetryPrivateWorkspaceAgentSessionGet,
  });
};
